import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { custReqApi } from './api'
import type { CustReqAdditionalDeliveryInput, CustReqCreateInput, CustReqItem, CustReqSrcOption, CustReqUpdateInput } from './types'

// 고객 요청(S16) 조회·변경 훅(K0 계약 4.3). 변경 성공 시 요청 캐시를 무효화한다.
// 호스트 화면의 캐시(예: scheduler 리스트 보기)는 options.invalidateKeys 로 함께 무효화한다.

export const CUST_REQ_KEY = 'cust-req'

export interface CustReqMutationOptions {
  /** 변경 성공 시 요청 캐시와 함께 무효화할 호스트 쿼리 키 목록 */
  invalidateKeys?: readonly QueryKey[]
}

/** 공정별 요청 목록 — taskId 가 없으면 조회하지 않는다 */
export function useCustReqList(taskId: number | null) {
  return useQuery<CustReqItem[]>({
    queryKey: [CUST_REQ_KEY, 'list', taskId],
    queryFn: ({ signal }) => custReqApi.list(taskId as number, signal),
    enabled: taskId != null,
    staleTime: 0,
    retry: false,
  })
}

/** 받은 경로 선택지 — 실패 시 오류 상태(하드코딩 폴백 없음) */
export function useCustReqSrcOptions() {
  return useQuery<CustReqSrcOption[]>({
    queryKey: [CUST_REQ_KEY, 'src-options'],
    queryFn: ({ signal }) => custReqApi.listSrcOptions(signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

function useInvalidateAfterChange(options?: CustReqMutationOptions) {
  const qc = useQueryClient()
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: [CUST_REQ_KEY] }),
    ...(options?.invalidateKeys ?? []).map(queryKey => qc.invalidateQueries({ queryKey })),
  ])
}

export function useCreateCustReq(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (input: CustReqCreateInput) => custReqApi.create(input),
    onSuccess: invalidate,
  })
}

export function useUpdateCustReq(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (v: { custReqId: number; input: CustReqUpdateInput }) => custReqApi.update(v.custReqId, v.input),
    onSuccess: invalidate,
  })
}

export function useDeleteCustReq(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (custReqId: number) => custReqApi.remove(custReqId),
    onSuccess: invalidate,
  })
}

export function useAddCustReqPhotos(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (v: { custReqId: number; files: File[] }) => custReqApi.addPhotos(v.custReqId, v.files),
    onSuccess: invalidate,
  })
}

export function useDeleteCustReqPhoto(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (v: { custReqId: number; photoId: number }) => custReqApi.removePhoto(v.custReqId, v.photoId),
    onSuccess: invalidate,
  })
}

/** 반장님께 추가 전달 — 성공 시 요청 캐시와 호스트 캐시(배지 등)를 무효화. 실패 시 무효화하지 않음(상태 불변) */
export function useAdditionalDeliverCustReq(options?: CustReqMutationOptions) {
  const invalidate = useInvalidateAfterChange(options)
  return useMutation({
    mutationFn: (input: CustReqAdditionalDeliveryInput) => custReqApi.additionalDelivery(input),
    onSuccess: invalidate,
  })
}
